import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Hub } from '@app/components/Hub';
import { OneOffFlow } from '@app/components/OneOffFlow';
import { SubscriptionFlow } from '@app/components/SubscriptionFlow';
import { Auth } from '@app/components/Auth';
import { AdminDashboard } from '@app/components/AdminDashboard';
import { CustomerList } from '@app/components/admin/CustomerList';
import { CustomerDetailsEvents } from '@app/components/admin/CustomerDetailsEvents';
import { EventDetails } from '@app/components/admin/EventDetails';
import { AdminResetPassword } from '@app/components/AdminResetPassword';
import { ProtectedRoute } from '@app/components/ProtectedRoute';
import { PasswordProtection } from '@app/components/PasswordProtection';
import { DevTools } from '@app/components/shared/DevTools';
import { GiftingLayout } from '@app/components/shared/GiftingLayout';
import { PublicGiftList } from '@app/components/PublicGiftList';
import { ClientApp } from '@app/components/client/ClientApp';
import { TrpcProvider } from '@app/providers/TrpcProvider';

function ConditionalDevTools() {
  const location = useLocation();
  const isPublicGiftList = location.pathname.startsWith('/gift-list/');
  
  if (isPublicGiftList) {
    return null;
  }
  
  return <DevTools />;
}

export function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const response = await fetch('/api/auth/verify', {
        credentials: 'include',
      });
      const result = await response.json();
      setIsAuthenticated(result.authenticated || false);
    } catch (error) {
      setIsAuthenticated(false);
    } finally {
      setAuthLoading(false);
    }
  };

  // Only check auth when accessing admin protected routes
  useEffect(() => {
    const currentPath = window.location.pathname;
    const isAdminRoute = currentPath.startsWith('/admin');
    
    if (isAdminRoute) {
      checkAuth();
    } else {
      // For non-admin routes, skip auth check
      setAuthLoading(false);
    }
  }, []);

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
  };

  return (
    <TrpcProvider>
      <PasswordProtection>
          <Router>
            <Routes>
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/landing-hub" element={
              <GiftingLayout>
                <Hub />
              </GiftingLayout>
            } />
            <Route path="/one-off/:step" element={
              <GiftingLayout>
                <OneOffFlow />
              </GiftingLayout>
            } />
            <Route path="/subscription/:step" element={
              <GiftingLayout>
                <SubscriptionFlow />
              </GiftingLayout>
            } />
            <Route 
              path="/login" 
              element={<ClientApp />}
            />
            <Route 
              path="/admin/login" 
              element={
                isAuthenticated ? 
                  <Navigate to="/admin" replace /> : 
                  <Auth onLogin={handleLogin} />
              } 
            />
            <Route 
              path="/admin/reset-password" 
              element={<AdminResetPassword />} 
            />
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute 
                  isAuthenticated={isAuthenticated} 
                  loading={authLoading}
                >
                  <CustomerList onLogout={handleLogout} />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/old" 
              element={
                <ProtectedRoute 
                  isAuthenticated={isAuthenticated} 
                  loading={authLoading}
                >
                  <AdminDashboard onLogout={handleLogout} />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/customers/:customerId/events" 
              element={
                <ProtectedRoute 
                  isAuthenticated={isAuthenticated} 
                  loading={authLoading}
                >
                  <CustomerDetailsEvents onLogout={handleLogout} />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/events/:eventId" 
              element={
                <ProtectedRoute 
                  isAuthenticated={isAuthenticated} 
                  loading={authLoading}
                >
                  <EventDetails onLogout={handleLogout} />
                </ProtectedRoute>
              } 
            />
            <Route path="/gift-list/:token" element={<PublicGiftList />} />
            <Route path="/customer/login" element={<Navigate to="/login" replace />} />
            <Route path="/customer/*" element={<ClientApp />} />
            <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <ConditionalDevTools />
          </Router>
        </PasswordProtection>
    </TrpcProvider>
  );
}

export default App;